import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import AppLayout from './components/layout/AppLayout';
import AuthLayout from './components/layout/AuthLayout';
import AdminLayout from './components/layout/AdminLayout';
import AdminRoute from './components/auth/AdminRoute';
import { TeamAccessGuard, RoleGuard, ProtectedRoute } from './components/auth';
import Login from './pages/auth/Login';
import AuthCallback from './pages/auth/callback';
import Unauthorized from './pages/auth/Unauthorized';
import { DashboardPage, TeamsPage, CreateTeamPage, TeamSetupPage, TeamSettingsPage, TeamSchedulingRulesPage, TeamStaffingRequirementsPage, TeamDashboardPage, TeamShiftTypesPage, TeamPreferencesPage, UserProfilePage } from './pages';
import TeamInvitePage from './pages/teams/TeamInvitePage';
import AcceptInvitationPage from './pages/invitations/AcceptInvitationPage';
import { TeamMembersPage } from './pages/teams/TeamMembersPage';
import TeamSchedulesPage from './pages/schedules/TeamSchedulesPage';
import NewSchedulePage from './pages/schedules/NewSchedulePage';
import ScheduleDetailPage from './pages/schedules/ScheduleDetailPage';
import EditSchedulePage from './pages/schedules/EditSchedulePage';
import { ConfirmationProvider } from './hooks/useConfirmation';

const App: React.FC = () => {
  // Common fallback for non-admin team members
  const adminOnlyMessage = (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="p-4 mb-4 text-sm text-red-700 bg-red-100 rounded-lg">
        You need administrator privileges to access this page.
      </div>
    </div>
  );

  return (
    <ConfirmationProvider>
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route element={<AuthLayout />}>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Login />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/unauthorized" element={<Unauthorized />} />
            <Route path="/invitations/:token" element={<AcceptInvitationPage />} />
          </Route>
          
          {/* Protected routes */}
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="profile" element={<UserProfilePage />} />
            
            <Route path="teams">
              <Route index element={<TeamsPage />} />
              <Route path="create" element={<CreateTeamPage />} />
              <Route 
                path=":teamId/setup" 
                element={
                  <TeamAccessGuard>
                    <RoleGuard roles={['admin']} fallback={adminOnlyMessage}>
                      <TeamSetupPage />
                    </RoleGuard>
                  </TeamAccessGuard>
                } 
              />
              <Route 
                path=":teamId/dashboard" 
                element={
                  <TeamAccessGuard>
                    <TeamDashboardPage />
                  </TeamAccessGuard>
                } 
              />
              <Route 
                path=":teamId/settings" 
                element={
                  <TeamAccessGuard>
                    <RoleGuard roles={['admin']} fallback={adminOnlyMessage}>
                      <TeamSettingsPage />
                    </RoleGuard>
                  </TeamAccessGuard>
                } 
              />
              <Route 
                path=":teamId/settings/scheduling" 
                element={
                  <TeamAccessGuard>
                    <RoleGuard roles={['admin']} fallback={adminOnlyMessage}>
                      <TeamSchedulingRulesPage />
                    </RoleGuard>
                  </TeamAccessGuard>
                } 
              />
              <Route 
                path=":teamId/settings/staffing" 
                element={
                  <TeamAccessGuard>
                    <RoleGuard roles={['admin']} fallback={adminOnlyMessage}>
                      <TeamStaffingRequirementsPage />
                    </RoleGuard>
                  </TeamAccessGuard>
                } 
              />
              <Route 
                path=":teamId/members/invite" 
                element={
                  <TeamAccessGuard>
                    <RoleGuard roles={['admin']} fallback={adminOnlyMessage}>
                      <TeamInvitePage />
                    </RoleGuard>
                  </TeamAccessGuard>
                } 
              />
              <Route 
                path=":teamId/members" 
                element={
                  <TeamAccessGuard>
                    <TeamMembersPage />
                  </TeamAccessGuard>
                } 
              />
              <Route 
                path=":teamId/shift-types" 
                element={
                  <TeamAccessGuard>
                    <RoleGuard roles={['admin']} fallback={adminOnlyMessage}>
                      <TeamShiftTypesPage />
                    </RoleGuard>
                  </TeamAccessGuard>
                } 
              />
              <Route 
                path=":teamId/preferences" 
                element={
                  <TeamAccessGuard>
                    <RoleGuard roles={['admin']} fallback={adminOnlyMessage}>
                      <TeamPreferencesPage />
                    </RoleGuard>
                  </TeamAccessGuard>
                } 
              />
              {/* Schedule routes */}
              <Route 
                path=":teamId/schedules" 
                element={
                  <TeamAccessGuard>
                    <TeamSchedulesPage />
                  </TeamAccessGuard>
                } 
              />
              <Route 
                path=":teamId/schedules/new" 
                element={
                  <TeamAccessGuard>
                    <RoleGuard roles={['admin']} fallback={adminOnlyMessage}>
                      <NewSchedulePage />
                    </RoleGuard>
                  </TeamAccessGuard>
                } 
              />
              <Route 
                path=":teamId/schedules/:scheduleId" 
                element={
                  <TeamAccessGuard>
                    <ScheduleDetailPage />
                  </TeamAccessGuard>
                } 
              />
              <Route 
                path=":teamId/schedules/:scheduleId/edit" 
                element={
                  <TeamAccessGuard>
                    <RoleGuard roles={['admin']} fallback={adminOnlyMessage}>
                      <EditSchedulePage />
                    </RoleGuard>
                  </TeamAccessGuard>
                } 
              />
            </Route>
          </Route>

          {/* Admin routes - using the new AdminRoute component */}
          <Route
            element={
              <AdminRoute>
                <AdminLayout />
              </AdminRoute>
            }
          >
            <Route path="admin" element={<div>Admin Dashboard</div>} />
          </Route>

          {/* Fallback - redirect to dashboard */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </ConfirmationProvider>
  );
};

export default App;
